/**
 * @file api-shop-avito.ts
 * @description API-клиент для Avito-кабинета магазина (phase14.0).
 *
 * Эндпоинты бэкенда (все возвращают `{ data: ... }`; apiFetch распаковывает
 * верхнее поле `data` автоматически, поэтому здесь возвращаем уже из него):
 *  - GET    /admin/shop/avito/status            → AvitoStatusDto
 *  - GET    /admin/shop/avito/sync-jobs         → { items, meta }
 *  - POST   /admin/shop/avito/sync/:kind        → { jobId, status }
 *  - GET    /admin/shop/avito/listings          → { items, meta }
 *  - GET    /admin/shop/avito/orders            → { items, meta }
 *  - GET    /admin/shop/avito/reviews           → { items, meta }
 */

import { apiFetch } from "./api-client";

export type AvitoSyncKind = "listings" | "orders" | "reviews";

export interface AvitoStatusDto {
  configured: boolean;
  counts: {
    listings: number;
    orders: number;
    reviews: number;
  };
  lastJob: {
    id: string;
    kind: string;
    status: string;
    startedAt: string;
    finishedAt: string | null;
    itemsProcessed: number;
    errorMessage: string | null;
  } | null;
}

export interface AvitoSyncJobDto {
  id: string;
  kind: string;
  trigger: string;
  status: string;
  startedAt: string;
  finishedAt: string | null;
  itemsProcessed: number;
  itemsCreated: number;
  itemsUpdated: number;
  errorMessage: string | null;
}

export interface AvitoListingDto {
  productId: string;
  displayName: string;
  status: string;
  avitoItemId: string;
  avitoStatus: string;
  url: string;
  address: string | null;
  pricePenny: string;
  lastSyncedAt: string;
  removedAt: string | null;
}

export interface AvitoListingComponentDto {
  id: string;
  position: number;
  name: string;
  unit: string | null;
  qty: number | null;
  description: string | null;
}

export interface AvitoListingDetailDto {
  productId: string;
  displayName: string;
  description: string | null;
  productStatus: string;
  productSlug: string;
  avitoItemId: string;
  avitoStatus: string;
  url: string;
  address: string | null;
  pricePenny: string;
  avitoCategory: { id: number; name: string } | null;
  images: { listing: string | null; list: string[] } | null;
  vas: unknown;
  firstSeenAt: string;
  lastSyncedAt: string;
  removedAt: string | null;
  components: AvitoListingComponentDto[];
}

export interface AvitoTestConnectionResult {
  ok: boolean;
  message: string;
}

export interface AvitoOrderDto {
  avitoOrderId: string;
  marketplaceId: string;
  avitoStatus: string;
  orderId: string;
  orderNumber: string;
  orderStatus: string;
  totalGross: string;
  createdAt: string;
  lastSyncedAt: string;
}

export interface AvitoReviewDto {
  id: string;
  avitoReviewId: string;
  score: number;
  text: string | null;
  senderName: string;
  stage: string | null;
  createdAtAvito: string;
  product: { id: string; displayName: string } | null;
}

export interface PagedResult<T> {
  items: T[];
  meta: { total: number; page: number };
}

interface AvitoSyncTriggerResult {
  jobId: string;
  status: string;
}

export const shopAvito = {
  status: () => apiFetch<AvitoStatusDto>("/admin/shop/avito/status"),

  testConnection: () =>
    apiFetch<AvitoTestConnectionResult>("/admin/shop/avito/test-connection", {
      method: "POST",
    }),

  listingDetail: (productId: string) =>
    apiFetch<AvitoListingDetailDto>(
      `/admin/shop/avito/listings/${encodeURIComponent(productId)}`,
    ),

  syncJobs: (params: { kind?: string; page?: number; perPage?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.kind) search.set("kind", params.kind);
    if (params.page) search.set("page", String(params.page));
    if (params.perPage) search.set("perPage", String(params.perPage));
    const qs = search.toString();
    return apiFetch<PagedResult<AvitoSyncJobDto>>(
      `/admin/shop/avito/sync-jobs${qs ? `?${qs}` : ""}`,
    );
  },

  triggerSync: (kind: AvitoSyncKind) =>
    apiFetch<AvitoSyncTriggerResult>(`/admin/shop/avito/sync/${kind}`, {
      method: "POST",
    }),

  listings: (params: { page?: number; perPage?: number; status?: string } = {}) => {
    const search = new URLSearchParams();
    if (params.page) search.set("page", String(params.page));
    if (params.perPage) search.set("perPage", String(params.perPage));
    if (params.status) search.set("status", params.status);
    const qs = search.toString();
    return apiFetch<PagedResult<AvitoListingDto>>(
      `/admin/shop/avito/listings${qs ? `?${qs}` : ""}`,
    );
  },

  orders: (params: { page?: number; perPage?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.page) search.set("page", String(params.page));
    if (params.perPage) search.set("perPage", String(params.perPage));
    const qs = search.toString();
    return apiFetch<PagedResult<AvitoOrderDto>>(
      `/admin/shop/avito/orders${qs ? `?${qs}` : ""}`,
    );
  },

  reviews: (params: { page?: number; perPage?: number; score?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.page) search.set("page", String(params.page));
    if (params.perPage) search.set("perPage", String(params.perPage));
    if (params.score) search.set("score", String(params.score));
    const qs = search.toString();
    return apiFetch<PagedResult<AvitoReviewDto>>(
      `/admin/shop/avito/reviews${qs ? `?${qs}` : ""}`,
    );
  },
};

/** Маппинг Avito-статуса листинга → label + tone. */
export function getAvitoStatusLabel(avitoStatus: string): {
  label: string;
  tone: "ok" | "warn" | "danger" | "muted";
} {
  switch (avitoStatus) {
    case "active":
      return { label: "Активный", tone: "ok" };
    case "old":
      return { label: "Старый", tone: "muted" };
    case "removed":
      return { label: "Снят", tone: "muted" };
    case "blocked":
      return { label: "Заблокирован", tone: "danger" };
    case "rejected":
      return { label: "Отклонён", tone: "warn" };
    default:
      return { label: avitoStatus, tone: "muted" };
  }
}

/** Перевод копеек в рубли (форматированных). */
export function formatPennyToRub(penny: string | number): string {
  const num = typeof penny === "string" ? Number.parseInt(penny, 10) : penny;
  if (!Number.isFinite(num)) return "—";
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(num / 100);
}
